export const articleCreatorList = {
  slug: 'usfans-creator-list',
  en: {
    title: 'USFans Creator List: Organize and Track the Creators You Follow',
    description:
      'Understand what a creator list is, how it differs from a directory, and how to keep your own list organized.',
    tag: 'Creators',
    intro: [
      'A creator list is a personal or shared set of creators you want to follow, track or revisit. Unlike a broad directory, a list is usually smaller and focused on what matters to you.',
      'This guide explains how creator lists work and how to build and maintain one that stays useful over time.',
    ],
    sections: [
      {
        title: 'What a creator list is',
        paragraphs: [
          'A creator list typically contains a name, a category, a short note and a link. The point is to keep your favorites in one place so you can find them again quickly.',
          'Lists can be private, shared with a group, or published for others to use as a starting point.',
        ],
      },
      {
        title: 'List vs directory',
        paragraphs: [
          'A directory tries to cover many creators for discovery, while a list is curated and focused on a specific set you care about.',
          'Starting from a directory and saving your picks into a list is a common, efficient workflow.',
        ],
      },
      {
        title: 'Keeping your list useful',
        paragraphs: [
          'Review and update your list regularly so it does not fill up with outdated or inactive entries. Add a short note to each entry to remind yourself why you saved it.',
          'Keep links to the original source so you can always check the latest information directly.',
        ],
      },
    ],
    faq: [
      { q: 'What is a USFans creator list?', a: 'A curated set of creators you want to follow, track or revisit, usually with a name, category, note and link.' },
      { q: 'How is a list different from a directory?', a: 'A directory covers many creators for discovery, while a list is curated and focused on a specific set.' },
      { q: 'How do I keep my list useful?', a: 'Review and update it regularly, add short notes, and keep links to the original source.' },
    ],
  },
  pl: {
    title: 'USFans Creator List: porządkuj i śledź twórców, których obserwujesz',
    description:
      'Zrozum, czym jest lista twórców, czym różni się od katalogu i jak utrzymać własną listę w porządku.',
    tag: 'Twórcy',
    intro: [
      'Lista twórców to osobisty lub wspólny zestaw twórców, których chcesz obserwować, śledzić lub do których chcesz wracać. W przeciwieństwie do szerokiego katalogu lista jest zwykle mniejsza i skupiona na tym, co ważne dla Ciebie.',
      'Ten przewodnik wyjaśnia, jak działają listy twórców i jak zbudować oraz utrzymać taką, która pozostaje przydatna.',
    ],
    sections: [
      {
        title: 'Czym jest lista twórców',
        paragraphs: [
          'Lista twórców zwykle zawiera nazwę, kategorię, krótką notatkę i link. Chodzi o to, by trzymać ulubionych w jednym miejscu i szybko ich odnajdywać.',
          'Listy mogą być prywatne, udostępniane grupie lub publikowane dla innych jako punkt wyjścia.',
        ],
      },
      {
        title: 'Lista a katalog',
        paragraphs: [
          'Katalog stara się objąć wielu twórców w celu odkrywania, a lista jest kuratorowana i skupiona na konkretnym zestawie, na którym Ci zależy.',
          'Zaczynanie od katalogu i zapisywanie wybranych pozycji do listy to popularny, efektywny sposób pracy.',
        ],
      },
      {
        title: 'Jak utrzymać listę przydatną',
        paragraphs: [
          'Regularnie przeglądaj i aktualizuj listę, aby nie zapełniała się nieaktualnymi lub nieaktywnymi wpisami. Dodawaj krótką notatkę, aby pamiętać, po co zapisałeś dany wpis.',
          'Zachowuj linki do oryginalnego źródła, aby zawsze móc sprawdzić najnowsze informacje.',
        ],
      },
    ],
    faq: [
      { q: 'Czym jest lista twórców USFans?', a: 'Kuratorowany zestaw twórców, których chcesz obserwować lub do których wracasz, zwykle z nazwą, kategorią, notatką i linkiem.' },
      { q: 'Czym lista różni się od katalogu?', a: 'Katalog obejmuje wielu twórców w celu odkrywania, a lista jest kuratorowana i skupiona na konkretnym zestawie.' },
      { q: 'Jak utrzymać listę przydatną?', a: 'Regularnie ją przeglądaj i aktualizuj, dodawaj krótkie notatki i zachowuj linki do źródła.' },
    ],
  },
  es: {
    title: 'Lista de creadores de USFans: organiza y sigue a los creadores que te interesan',
    description:
      'Entiende qué es una lista de creadores, en qué se diferencia de un directorio y cómo mantener tu propia lista organizada.',
    tag: 'Creadores',
    intro: [
      'Una lista de creadores es un conjunto personal o compartido de creadores que quieres seguir, rastrear o volver a visitar. A diferencia de un directorio amplio, una lista suele ser más pequeña y enfocada.',
      'Esta guía explica cómo funcionan las listas de creadores y cómo crear y mantener una que siga siendo útil.',
    ],
    sections: [
      {
        title: 'Qué es una lista de creadores',
        paragraphs: [
          'Una lista suele contener un nombre, una categoría, una nota breve y un enlace. La idea es mantener tus favoritos en un solo lugar para encontrarlos rápido.',
          'Las listas pueden ser privadas, compartidas con un grupo o publicadas para que otros las usen como punto de partida.',
        ],
      },
      {
        title: 'Lista vs directorio',
        paragraphs: [
          'Un directorio intenta cubrir muchos creadores para el descubrimiento, mientras que una lista es curada y se centra en un conjunto concreto que te importa.',
          'Empezar desde un directorio y guardar tus elegidos en una lista es un flujo común y eficiente.',
        ],
      },
      {
        title: 'Mantener útil tu lista',
        paragraphs: [
          'Revisa y actualiza tu lista con regularidad para que no se llene de entradas obsoletas o inactivas. Añade una nota breve para recordar por qué guardaste cada entrada.',
          'Guarda enlaces a la fuente original para poder consultar siempre la información más reciente.',
        ],
      },
    ],
    faq: [
      { q: '¿Qué es una lista de creadores de USFans?', a: 'Un conjunto curado de creadores que quieres seguir o revisar, normalmente con nombre, categoría, nota y enlace.' },
      { q: '¿En qué se diferencia una lista de un directorio?', a: 'El directorio cubre muchos creadores para descubrir, mientras que la lista es curada y se centra en un conjunto concreto.' },
      { q: '¿Cómo mantengo útil mi lista?', a: 'Revísala y actualízala con regularidad, añade notas breves y guarda enlaces a la fuente.' },
    ],
  },
  fr: {
    title: 'Liste des créateurs USFans : organiser et suivre vos créateurs',
    description:
      'Comprenez ce qu’est une liste de créateurs, en quoi elle diffère d’un annuaire et comment garder votre liste organisée.',
    tag: 'Créateurs',
    intro: [
      'Une liste de créateurs est un ensemble personnel ou partagé de créateurs à suivre, suivre ou revoir. Contrairement à un annuaire large, une liste est souvent plus petite et ciblée.',
      'Ce guide explique comment fonctionnent les listes et comment créer et maintenir une liste qui reste utile.',
    ],
    sections: [
      {
        title: 'Ce qu’est une liste de créateurs',
        paragraphs: [
          'Une liste contient généralement un nom, une catégorie, une note brève et un lien. L’idée est de garder vos favoris au même endroit pour les retrouver vite.',
          'Les listes peuvent être privées, partagées ou publiées pour servir de point de départ à d’autres.',
        ],
      },
      {
        title: 'Liste vs annuaire',
        paragraphs: [
          'Un annuaire cherche à couvrir de nombreux créateurs pour la découverte, tandis qu’une liste est curée et ciblée sur un ensemble précis.',
          'Commencer par un annuaire puis enregistrer ses choix dans une liste est un flux courant et efficace.',
        ],
      },
      {
        title: 'Garder votre liste utile',
        paragraphs: [
          'Révisez et mettez à jour votre liste régulièrement pour éviter les entrées obsolètes ou inactives. Ajoutez une note brève pour vous rappeler pourquoi vous l’avez enregistrée.',
          'Gardez des liens vers la source d’origine pour consulter les informations les plus récentes.',
        ],
      },
    ],
    faq: [
      { q: 'Qu’est-ce qu’une liste de créateurs USFans ?', a: 'Un ensemble curé de créateurs à suivre ou à revoir, généralement avec nom, catégorie, note et lien.' },
      { q: 'En quoi une liste diffère d’un annuaire ?', a: 'L’annuaire couvre de nombreux créateurs pour la découverte, tandis que la liste est curée et ciblée.' },
      { q: 'Comment garder ma liste utile ?', a: 'Révisez et mettez à jour régulièrement, ajoutez des notes brèves et gardez des liens vers la source.' },
    ],
  },
};